import {
  CARESEEKER_STATUS_ACTIVE,
  CARESEEKER_STATUS_CREATED,
  CARESEEKER_STATUS_INACTIVE,
  CARESEEKER_STATUS_LOST,
  CARESEEKER_STATUS_ONBOARDING,
  PRODUCT_FEATURES,
} from "core/consts";
import { Careseeker, SearchType } from "core/types";
import { useLoggedCareseeker } from "reduxentities/hooks";
import { getProductFeatureActive } from "../productfeatures";
import { ACCENT_COLOR, ERROR_COLOR, SUCCESS_COLOR } from "../utils/colors";
import { activateNewFormDesign } from "../utils/featureFlags";

export function defaultSearchType(
  careseeker: Careseeker | undefined,
): SearchType | null {
  if (careseeker?.patient_types?.length == 1)
    return careseeker?.patient_types[0];

  return null;
}

export function getCareseekerStatusColor(status: number | undefined) {
  switch (status) {
    case CARESEEKER_STATUS_ACTIVE:
      return SUCCESS_COLOR;
    case CARESEEKER_STATUS_INACTIVE:
    case CARESEEKER_STATUS_LOST:
      return ERROR_COLOR;
    case CARESEEKER_STATUS_ONBOARDING:
      return ACCENT_COLOR;
    case CARESEEKER_STATUS_CREATED:
      return null;
    default:
      return null;
  }
}

export function onPremHttpRequest({
  body,
  method,
  on_premise_authorization_token,
  on_premise_domain_ssl,
  path,
}: {
  body?: BodyInit;
  method: string;
  on_premise_authorization_token: string | undefined;
  on_premise_domain_ssl: string | undefined;
  path: string;
}) {
  const headers: HeadersInit = {};

  if (on_premise_authorization_token) {
    // username is empty.
    const auth = btoa(`:${on_premise_authorization_token}`);
    headers["Authorization"] = `Basic ${auth}`;
  }

  return fetch(`https://${on_premise_domain_ssl}/${path}`, {
    body,
    headers,
    method,
  });
}

export function hasSSOActivated(careseeker: Careseeker | undefined): boolean {
  const ssoIntegration = careseeker?.sso_integration;
  if (!ssoIntegration) return false;

  return ssoIntegration?.active;
}

export function hasSSOConsentGiven(
  careseeker: Careseeker | undefined,
): boolean {
  const ssoIntegration = careseeker?.sso_integration;
  if (!ssoIntegration) return false;

  return !!(
    ssoIntegration?.consent ?? ssoIntegration?.config?.deactivate_consent_flow
  );
}

export function hasSSOActivatedAndConsentGiven(
  careseeker: Careseeker | undefined,
): boolean {
  const ssoIntegration = careseeker?.sso_integration;
  if (!ssoIntegration) return false;

  return hasSSOActivated(careseeker) && hasSSOConsentGiven(careseeker);
}

export const useIsDocsAndExtractActive = () => {
  const careseeker = useLoggedCareseeker();
  const isDocsAndExtractActive = getProductFeatureActive(
    careseeker?.product_features,
    PRODUCT_FEATURES.DOCS_AND_EXTRACT,
  );
  const isDischargeLetterActive = getProductFeatureActive(
    careseeker?.product_features,
    PRODUCT_FEATURES.DISCHARGE_LETTER,
  );

  return {
    isDischargeLetterActive,
    isDocsAndExtractActive,
    isRedesignActive:
      activateNewFormDesign() ||
      isDocsAndExtractActive ||
      isDischargeLetterActive,
  } as const;
};
